/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../../logger';
import { debugPixel, isAstAvailable } from '../../../util';
import { Placement } from '../../placement';
import { IAB_STANDARD_BANNER_SIZES } from '../../placement/constants';

import type { CampaignData, RenderedAdData } from '../../../types/index';
import type { PrebidAdUnit } from '../../../types/prebid';
import type { ApnAdObject, ApnTagObject } from '../../../types/vendor/ast';
import type { SiteConfig } from '../../config';

const logger = Logger.getInstance();

enum AdSource {
  RTB = 'rtb',
  CLIENT_MEDIATION = 'csm',
}

class AstPlacement extends Placement {
  private interactionListenerAdded: boolean = false;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig
  ) {
    super(adUnit, adUnitTransformer, campaignData, siteConfig);
    logger.debug(`AST Placement created for ${this.adUnitCode}`);
  }

  protected setSlot(): void {
    if (this.slot) {
      return;
    }
    if (isAstAvailable()) {
      logger.debug(`Determining AST tag for adUnit: ${this.adUnitCode}`);
      try {
        const allTags: Record<string, ApnTagObject> = window.apntag?.getTag() || {};
        const matchingTag = Object.values(allTags).find((tag: ApnTagObject) => {
          logger.debug(
            `Checking AST tag ${tag.invCode} for invCode match against ${this.adUnitCode}`
          );
          return tag.invCode === this.adUnitCode;
        });
        this.slot = matchingTag || null;
        if (this.slot) {
          logger.info(`AST tag located for ${this.adUnitCode}`);
        }
      } catch (e) {
        logger.error(`Error getting AST tag for ${this.adUnitCode}: ${e}`);
        this.slot = null;
      }
    } else {
      logger.error(
        `AST tag not available, unable to determine slot for adUnit: ${this.adUnitCode}`
      );
    }
  }

  protected setSupportedSizes(): void {
    const iabSizes = IAB_STANDARD_BANNER_SIZES.map((s) => {
      const [w, h] = s.split('x').map(Number);
      return { width: w, height: h };
    });
    try {
      const apnTag = this.slot as ApnTagObject;
      if (apnTag?.sizes) {
        apnTag.sizes.forEach((size: number[]) => {
          // Only consider sizes that match a standard IAB banner size
          if (iabSizes.some((s) => s.width === size[0] && s.height === size[1])) {
            this.addSupportedSize({ width: size[0], height: size[1] });
          }
        });
      }
    } catch (e) {
      logger.warn(`Error determining AST tag size: ${e}`);
    }
  }

  protected determineVisibility(): void {
    const size = this.getSmallestSupportedSize();
    if (!size || this.slot === null) {
      return;
    }

    const apnTag = this.slot as ApnTagObject;
    const element = document.getElementById(apnTag.targetId);
    if (!element) {
      logger.info(`AST adunit '${this.adUnitCode}' target element '${apnTag.targetId}' not found`);
      return;
    }

    const rect = element.getBoundingClientRect();
    const elementArea = size.width * size.height;

    if (elementArea === 0) {
      return;
    }

    const adLeft = rect.left + (rect.width - size.width) / 2;
    const adTop = rect.top + (rect.height - size.height) / 2;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const visibleWidth = Math.max(
      0,
      Math.min(adLeft + size.width, viewportWidth) - Math.max(adLeft, 0)
    );

    const visibleHeight = Math.max(
      0,
      Math.min(adTop + size.height, viewportHeight) - Math.max(adTop, 0)
    );

    const visibleArea = visibleWidth * visibleHeight;

    if (visibleArea >= elementArea / 2) {
      this.isVisible(true);
    } else {
      this.isVisible(false);
    }
  }

  protected setWrappingDomElement(): void {
    if (this.slot) {
      const apnTag = this.slot as ApnTagObject;
      this.wrappingDomElement = document.getElementById(apnTag.utDivId);
    }
  }

  protected addListeners(): void {
    logger.debug(`Adding AST listeners for placement: ${this.adUnitCode}`);
    this.addApnAdSlotRenderStartListener();
    this.addViewableImpressionListener();
    this.addPrebidAdRenderedListener();
  }

  protected addAdInteractionListener(): void {
    if (this.slot === null || this.interactionListenerAdded) {
      return;
    }
    logger.info(`Adding ad interaction listener for ${this.adUnitCode}`);
    const errorPrefix = `Unable to add ad interaction listener for ${this.adUnitCode}:`;
    const apnTag = this.slot as ApnTagObject;
    const slotParent = document.getElementById(apnTag.targetId);

    if (slotParent) {
      const placementFrame = slotParent.querySelector('iframe');
      if (placementFrame) {
        try {
          placementFrame.contentWindow?.document.addEventListener('click', () => {
            if (this.dispatchAdInteraction()) {
              debugPixel({
                en: 'AdInteractionTriggerApnFrameContentWindowClick',
              });
            }
          });
          logger.debug(`Added click listener for APN frame: ${this.adUnitCode}`);
        } catch (e) {
          logger.debug(
            `Unable to add interaction listeners to APN placement frame for ${this.adUnitCode}, error: ${e}`
          );
        }
      } else {
        logger.warn(`${errorPrefix} ad iframe not found`);
      }
    } else {
      logger.warn(`${errorPrefix} iframe parent element not found`);
    }
    this.interactionListenerAdded = true;
  }

  private addApnAdSlotRenderStartListener(): void {
    if (!this.slot) {
      return;
    }

    try {
      const apnTag = this.slot as ApnTagObject;
      logger.debug(`Registering adSlotRenderStart listener on apntag  for: ${this.adUnitCode}`);

      apnTag.onEvent('adSlotRenderStart', apnTag.targetId, (adObj: ApnAdObject) => {
        if (!this.slotRenderedForOpportunity) {
          this.slotRenderedForOpportunity = true;
          this.interactionListenerAdded = false;

          let deltaSeconds = Math.min(Math.round(this.currentOpportunity!.age / 1000), 20);
          debugPixel({
            en: 'apnAdSlotRenderStart',
            adUnitCode: this.adUnitCode,
            creativeId: String(adObj.creativeId),
            renderTimeDelta: String(deltaSeconds),
          });

          logger.debug(
            `APN ad render start for: ${adObj.targetId}, handled by placement: ${this.adUnitCode}, creative: ${adObj.creativeId}`
          );

          let size = { width: adObj.width ?? 0, height: adObj.height ?? 0 };
          if (adObj.banner) {
            size = { width: adObj.banner.width, height: adObj.banner.height };
          }

          if (adObj.source !== AdSource.RTB) {
            const renderedAdData: RenderedAdData = {
              advertiserId: '', // Not available from APN
              lineItemId: '', // Not available from APN
              creativeId: String(adObj.creativeId),
              isProgrammaticBuy: false,
              responseIdentifier: adObj.uuid,
              size: size,
            };
            this.handleRenderedAdData(renderedAdData);
          } else if (adObj.source === AdSource.RTB) {
            debugPixel({
              en: 'apnSlotRenderedRTBAd',
              adUnitCode: this.adUnitCode,
              buyerMemberId: String(adObj.buyerMemberId),
            });
          }
        }
      });
    } catch (e) {
      logger.error(`Error adding APN adLoaded listener to ${this.adUnitCode}: ${e}`);
    }
  }

  private addViewableImpressionListener(): void {
    if (this.slot === null) {
      return;
    }
    try {
      const apnTag = this.slot as ApnTagObject;
      logger.debug(`Registering impressionViewable listener on apntag for: ${this.adUnitCode}`);
      apnTag.onEvent('mrcView', apnTag.targetId, (adObj: ApnAdObject) => {
        logger.debug(
          `impressionViewable dispatched by apntag for: ${adObj.targetId}, ${this.adUnitCode}`
        );
        this.dispatchViewableImpression();
      });
    } catch (e) {
      logger.error(`Error adding APN viewable impression listener to ${this.adUnitCode}: ${e}`);
    }
  }
}

export { AstPlacement };
